import React from 'react';
import { getConfigValue, getWebAppUrl } from '../config/runtimeConfig';
import { Container, TextField, Button, Typography, Paper, CircularProgress, Box, Avatar, styled } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useAuth } from '../contexts/authContext';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Helmet } from 'react-helmet-async';

const styles = {
  textCenter: {
    textAlign: 'center',
    color: '#666',
  },
  smallText: {
    fontSize: '0.8rem',
  },
};

const RedAsterisk = styled('span')({
  color: 'red',
});

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  password: Yup.string()
    .required('Password is required'),
});

const Login = () => {
  const { login } = useAuth();

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      try {
        await login(values.email, values.password);
      } catch (error) {
        console.error('Error logging in:', error);
        formik.setErrors({ general: 'Invalid email or password' });
      }
    },
  });

  const handleForgetPassword = () => {
    const tenantName = getConfigValue('REACT_APP_TENANT_NAME');
    const policyName = getConfigValue('REACT_APP_PASSWORD_RESET_POLICY');
    const clientId = getConfigValue('REACT_APP_CLIENT_ID');
    const redirectUri = encodeURIComponent(`${getWebAppUrl()}/password-reset-success`);
    const state = encodeURIComponent(
      JSON.stringify({ from: 'passwordReset', passwordResetSuccess: true })
    );
    const nonce = Date.now().toString();
    const scope = 'openid';
    const responseType = 'id_token';
    const prompt = 'login';
    const passwordResetUrl = `https://${tenantName}.b2clogin.com/${tenantName}.onmicrosoft.com/oauth2/v2.0/authorize?p=${policyName}&client_id=${clientId}&nonce=${nonce}&redirect_uri=${redirectUri}&scope=${scope}&response_type=${responseType}&prompt=${prompt}&state=${state}`;

    window.location.href = passwordResetUrl;
  };

  return (
    <Box
      style={{
        height: '100vh',
        background: 'linear-gradient(-20deg, #616161 0%, #9bc5c3 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        position: 'fixed',
        width: '100%',
        top: 0,
        left: 0,
        overflow: 'hidden',
      }}
    >
      <Helmet>
        <title>Login</title>
      </Helmet>
      <Container component={Paper} elevation={6} maxWidth="xs" style={{
        padding: 30,
        borderRadius: '15px',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
        position: 'relative',
        zIndex: 1,
      }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container justifyContent="center" alignItems="center">
            <Grid item>
              <Avatar src='/cirapng.png' alt="Logo" style={{
                width: 80,
                height: 100,
                marginBottom: 20,
                overflow: 'visible',
              }} />
            </Grid>
          </Grid>
          <Typography variant="h5" component="h2" gutterBottom style={{ textAlign: 'center', fontWeight: 'bold', color: '#333', marginBottom: 10 }}>
            Welcome
          </Typography>
          <Typography variant="subtitle1" component="h2" gutterBottom style={{ ...styles.textCenter, ...styles.smallText, marginBottom: 15 }}>
            Login to Credential Intelligence Recognition Assistant
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={
                  <>
                    {'email'} <RedAsterisk>*</RedAsterisk>
                  </>
                }
                name="email"
                variant="outlined"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={
                  <>
                    {'password'} <RedAsterisk>*</RedAsterisk>
                  </>
                }
                name="password"
                type="password"
                variant="outlined"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
              />
            </Grid>
          </Grid>
          {formik.errors.general && (
            <Typography color="error" style={{ marginTop: 15, textAlign: 'center' }}>
              {formik.errors.general}
            </Typography>
          )}
          <Grid container justifyContent="flex-end" style={{ marginTop: 15, marginBottom: 15 }}>
            <Button
              onClick={handleForgetPassword}
              style={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              Forgot Password?
            </Button>
          </Grid>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '12px',
              gap: '8px',
            }}
            disabled={formik.isSubmitting}
          >
            Login
            {formik.isSubmitting && <CircularProgress size={15} color="inherit" />}
          </Button>
        </form>
      </Container>
    </Box>
  );
};

export default Login;
